import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, ArrowUpRight, InstagramLogo, TiktokLogo, YoutubeLogo, LinkedinLogo, FacebookLogo, ShoppingBag, PaperPlaneTilt, Wallet, ShoppingCart, Plus, Minus, X, Tag } from "@phosphor-icons/react";
import client from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import InstallPrompt from "@/components/InstallPrompt";

import { MusicListen, MUSIC_BY_ID } from "@/components/MusicPlatforms";

const platformIcon = { instagram: InstagramLogo, tiktok: TiktokLogo, youtube: YoutubeLogo, linkedin: LinkedinLogo, facebook: FacebookLogo };

const GFONT = {
  "Clash Display": null,
  "Bebas Neue": "Bebas+Neue",
  "Playfair Display": "Playfair+Display:wght@400;700;900",
  "Space Grotesk": "Space+Grotesk:wght@400;500;700",
  "Manrope": "Manrope:wght@400;500;700;800",
  "Poppins": "Poppins:wght@400;600;800",
  "Inter": "Inter:wght@400;500;700",
  "IBM Plex Sans": null,
  "IBM Plex Mono": null,
  "JetBrains Mono": "JetBrains+Mono:wght@400;700",
  "Lora": "Lora:wght@400;700",
  "Archivo Black": "Archivo+Black",
  "DM Sans": "DM+Sans:wght@400;500;700",
};

const injectGoogleFont = (family) => {
  const slug = GFONT[family];
  if (!slug) return;
  const id = `gf-${slug}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${slug}&display=swap`;
  document.head.appendChild(link);
};

const buildBg = (theme) => {
  if (!theme) return {};
  const t = theme.bg_type;
  if (t === "image" && theme.bg_image) return { backgroundImage: `url(${theme.bg_image})`, backgroundSize: "cover", backgroundPosition: "center" };
  if (t === "gradient" && theme.bg_gradient) return { background: theme.bg_gradient };
  if (t === "solid" && theme.bg_color) return { backgroundColor: theme.bg_color };
  return {};
};

const bundleDiscountPct = (distinct) => distinct >= 4 ? 15 : distinct === 3 ? 10 : distinct === 2 ? 5 : 0;

const cartKey = (slug) => `korp-cart-${slug}`;

const PublicBio = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", message: "" });
  const [openLead, setOpenLead] = useState(false);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey(slug)) || "{}"); } catch { return {}; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({ buyer_name: "", buyer_email: "", provider: "paypal" });
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => { client.get(`/public/${slug}`).then(r => setData(r.data)).catch(() => setNotFound(true)); }, [slug]);

  useEffect(() => {
    const th = data?.theme;
    if (th?.font_display) injectGoogleFont(th.font_display);
    if (th?.font_body) injectGoogleFont(th.font_body);
  }, [data?.theme]);

  // Persist cart
  useEffect(() => {
    try { localStorage.setItem(cartKey(slug), JSON.stringify(cart)); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("cart save failed", err); }
  }, [cart, slug]);

  const clickLink = async (link) => {
    try { await client.post(`/public/${slug}/click`, { link_id: link.id }); } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("click log failed:", err); }
    window.open(link.url, "_blank", "noopener,noreferrer");
  };

  const submitLead = async (e) => {
    e.preventDefault();
    try { await client.post(`/public/${slug}/lead`, lead); toast.success("Thanks — we'll be in touch."); setLead({ name: "", email: "", phone: "", message: "" }); setOpenLead(false); }
    catch { toast.error("Failed to submit"); }
  };

  // Cart helpers
  const products = data?.products || [];
  const cartLines = useMemo(() => {
    return Object.entries(cart)
      .map(([pid, qty]) => {
        const p = products.find(x => x.id === pid);
        if (!p || qty < 1) return null;
        return { ...p, qty, line_total: p.price * qty };
      })
      .filter(Boolean);
  }, [cart, products]);
  const cartCount = cartLines.reduce((s, l) => s + l.qty, 0);
  const distinctCount = cartLines.length;
  const subtotal = cartLines.reduce((s, l) => s + l.line_total, 0);
  const bundleEnabled = data?.profile?.bundle_discount_enabled;
  const discountPct = bundleEnabled ? bundleDiscountPct(distinctCount) : 0;
  const discountAmount = subtotal * (discountPct / 100);
  const total = subtotal - discountAmount;
  const nextTier = bundleEnabled ? (distinctCount === 1 ? { need: 1, pct: 5 } : distinctCount === 2 ? { need: 1, pct: 10 } : distinctCount === 3 ? { need: 1, pct: 15 } : null) : null;

  const addToCart = (product) => {
    setCart(prev => ({ ...prev, [product.id]: (prev[product.id] || 0) + 1 }));
    toast.success(`Added ${product.name} to cart`);
    setCartOpen(true);
  };
  const setQty = (pid, qty) => {
    setCart(prev => {
      const next = { ...prev };
      if (qty <= 0) delete next[pid];
      else next[pid] = Math.min(99, qty);
      return next;
    });
  };
  const clearCart = () => setCart({});

  const needsShipping = cartLines.some(l => l.requires_shipping);

  const checkout = async (e) => {
    e.preventDefault();
    if (cartLines.length === 0) { toast.error("Cart is empty"); return; }
    if (needsShipping) {
      const s = checkoutForm.shipping || {};
      const req = ["full_name", "line1", "city", "postal_code", "country"];
      for (const k of req) {
        if (!s[k] || !s[k].trim()) {
          toast.error("Please complete the shipping address");
          return;
        }
      }
    }
    setCheckingOut(true);
    try {
      const payload = {
        items: cartLines.map(l => ({ product_id: l.id, qty: l.qty })),
        buyer_name: checkoutForm.buyer_name.trim(),
        buyer_email: checkoutForm.buyer_email.trim(),
        provider: checkoutForm.provider,
      };
      if (needsShipping) payload.shipping_address = checkoutForm.shipping;
      const { data: order } = await client.post(`/public/${slug}/checkout-cart`, payload);
      clearCart();
      nav(`/shop-checkout/${order.order_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    }
    setCheckingOut(false);
  };

  const theme = data?.theme || {};
  const accent = theme.accent_color || "#FACC15";
  const bg = useMemo(() => buildBg(theme), [theme]);
  const fontDisplay = theme.font_display || "Clash Display";
  const fontBody = theme.font_body || "IBM Plex Sans";
  const cardStyle = theme.card_style || "brutal";
  const cardClass =
    cardStyle === "rounded" ? "rounded-2xl" :
    cardStyle === "glass" ? "rounded-xl backdrop-blur-md bg-white/5" :
    "";

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A0D14] text-white flex items-center justify-center noise">
        <div className="text-center">
          <div className="font-display text-6xl tracking-tighter mb-4">404</div>
          <div className="font-mono-k text-[#A1A1AA]">This page doesn't exist.</div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0A0D14]" />;

  const { profile, links, social } = data;

  return (
    <div
      className={`min-h-screen text-white ${theme.bg_type && theme.bg_type !== "brutalist" ? "" : "bg-[#0A0D14] noise"}`}
      style={{ ...bg, fontFamily: `"${fontBody}", ui-sans-serif, system-ui`, "--accent": accent }}
      data-testid="publicbio-root"
    >
      <InstallPrompt />
      <div className="max-w-4xl mx-auto p-6 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <a href="/" className="flex items-center gap-2 opacity-70 hover:opacity-100">
            <BrandLogo height={32} />
          </a>
          <div className="flex items-center gap-3">
            {profile.show_shop !== false && products?.length > 0 && (
              <button
                onClick={() => setCartOpen(true)}
                className="relative flex items-center gap-2 px-3 py-2 border border-white/20 hover:border-[color:var(--accent)] transition-colors"
                data-testid="cart-header-btn"
                aria-label="Open cart"
              >
                <ShoppingCart size={16} weight="bold" />
                <span className="font-mono-k text-xs tracking-widest-k">CART</span>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 flex items-center justify-center text-[10px] font-bold" style={{ background: accent, color: "#050505" }} data-testid="cart-badge">
                    {cartCount}
                  </span>
                )}
              </button>
            )}
            <div className="text-xs font-mono-k tracking-widest-k text-white/60">@{profile.slug}</div>
          </div>
        </div>

        <div className="grid grid-cols-6 auto-rows-[minmax(72px,auto)] gap-3">
          {/* Profile */}
          <div className={`col-span-6 border border-white/10 bg-black/40 p-8 flex items-center gap-6 brutal-shadow ${cardClass}`} data-testid="bio-profile">
            {profile.avatar ? (
              <img src={profile.avatar} alt="" className={`w-20 h-20 md:w-28 md:h-28 object-cover border border-white/20 ${cardStyle === "rounded" ? "rounded-full" : ""}`} />
            ) : (
              <div className={`w-20 h-20 md:w-28 md:h-28 border border-white/20 bg-black/50 flex items-center justify-center text-4xl ${cardStyle === "rounded" ? "rounded-full" : ""}`} style={{ color: accent, fontFamily: `"${fontDisplay}", ui-sans-serif` }}>
                {profile.name?.[0]?.toUpperCase() || "K"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-mono-k tracking-widest-k mb-2" style={{ color: accent }}>— {profile.title || `${profile.plan?.toUpperCase()} MEMBER`}</div>
              <h1 className="text-3xl md:text-5xl tracking-tighter leading-none" style={{ fontFamily: `"${fontDisplay}", ui-sans-serif` }}>{profile.name}</h1>
              {profile.bio && <p className="text-sm text-white/80 mt-3 max-w-lg">{profile.bio}</p>}
            </div>
          </div>

          {profile.show_about !== false && profile.about && (
            <div className={`col-span-6 border border-white/10 bg-black/40 p-6 ${cardClass}`} data-testid="bio-about">
              <div className="text-xs font-mono-k tracking-widest-k text-white/60 mb-3">— ABOUT</div>
              <p className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{profile.about}</p>
            </div>
          )}

          {profile.show_social !== false && <MusicListen items={social || []} cardClass={cardClass} />}

          {profile.show_social !== false && social?.some(s => !MUSIC_BY_ID[s.platform]) && (
            <div className={`col-span-6 md:col-span-3 border border-white/10 bg-black/40 p-6 ${cardClass}`}>
              <div className="text-xs font-mono-k tracking-widest-k text-white/60 mb-4">— FOLLOW</div>
              <div className="grid grid-cols-3 gap-2">
                {social.filter(s => !MUSIC_BY_ID[s.platform]).map(s => {
                  const I = platformIcon[s.platform];
                  return (
                    <a key={s.platform} href={s.profile_url || "#"} target="_blank" rel="noreferrer" className={`border border-white/10 p-3 flex flex-col items-center gap-1 hover:border-white transition-colors ${cardClass}`} data-testid={`bio-social-${s.platform}`}>
                      {I && <I size={22} weight="duotone" style={{ color: accent }} />}
                      <div className="text-[10px] font-mono-k text-white/60 truncate w-full text-center">@{s.handle}</div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {profile.show_tip_jar !== false && profile.wallet_address && (
            <div className={`col-span-6 md:col-span-3 border border-white/10 bg-black/40 p-6 ${cardClass}`}>
              <div className="text-xs font-mono-k tracking-widest-k text-white/60 mb-3">— TIP JAR</div>
              <div className="text-sm mb-2">Send crypto directly to this creator:</div>
              <code className="block bg-black/60 border border-white/10 p-2 text-xs break-all font-mono-k" style={{ color: accent }} data-testid="bio-wallet">{profile.wallet_address}</code>
            </div>
          )}

          <div className={`col-span-6 md:col-span-4 border p-8 ${cardClass}`} style={{ background: accent, color: "#050505", borderColor: accent }}>
            <div className="text-xs font-mono-k tracking-widest-k mb-2">— LET'S TALK</div>
            <div className="text-2xl md:text-3xl tracking-tighter mb-4" style={{ fontFamily: `"${fontDisplay}", ui-sans-serif` }}>Book a call or shoot a message.</div>
            <button onClick={() => setOpenLead(!openLead)} className="border border-black bg-black px-4 py-2 font-mono-k text-xs tracking-widest-k hover:bg-[#111318]" style={{ color: accent }} data-testid="bio-open-lead">
              {openLead ? "CLOSE" : "OPEN FORM"} <PaperPlaneTilt size={12} className="inline ml-1" />
            </button>
            {openLead && (
              <form onSubmit={submitLead} className="mt-4 grid gap-2 bg-white/10 p-3">
                <input required placeholder="Name" value={lead.name} onChange={e => setLead({ ...lead, name: e.target.value })} className="k-input bg-black text-white" data-testid="bio-lead-name" />
                <input required type="email" placeholder="Email" value={lead.email} onChange={e => setLead({ ...lead, email: e.target.value })} className="k-input bg-black text-white" data-testid="bio-lead-email" />
                <textarea placeholder="Message" rows={2} value={lead.message} onChange={e => setLead({ ...lead, message: e.target.value })} className="k-input bg-black text-white" data-testid="bio-lead-message" />
                <button className="border border-black bg-black py-2 font-mono-k text-xs tracking-widest-k" style={{ color: accent }} data-testid="bio-lead-submit">SUBMIT</button>
              </form>
            )}
          </div>

          <div className={`col-span-6 md:col-span-2 border border-white/10 bg-black/40 p-6 ${cardClass}`}>
            <div className="text-xs font-mono-k tracking-widest-k text-white/60 mb-3">— BUILT ON</div>
            <div className="text-3xl tracking-tighter" style={{ color: accent, fontFamily: `"${fontDisplay}", ui-sans-serif` }}>KORPORATE</div>
            <a href="/" className="text-xs font-mono-k text-white/60 hover:text-white mt-2 inline-flex items-center gap-1">Make yours <ArrowUpRight size={12} /></a>
          </div>

          {profile.show_links !== false && links?.length > 0 && (
            <div className="col-span-6 grid gap-3">
              <div className="text-xs font-mono-k tracking-widest-k text-white/60 mt-4">— LINKS</div>
              {links.map((l) => (
                <button key={l.id} onClick={() => clickLink(l)} className={`border border-white/10 bg-black/40 hover:opacity-90 transition-all p-5 flex items-center gap-4 text-left group ${cardClass}`} data-testid={`bio-link-${l.id}`}>
                  {l.image ? (
                    <img src={l.image} alt="" className={`w-16 h-16 object-cover flex-shrink-0 ${cardStyle === "rounded" ? "rounded-xl" : cardStyle === "glass" ? "rounded-lg" : ""}`} />
                  ) : (
                    <div className={`w-16 h-16 border border-white/20 flex items-center justify-center flex-shrink-0 ${cardStyle === "rounded" ? "rounded-xl" : ""}`} style={{ color: accent }}>
                      <Lightning size={22} weight="fill" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xl md:text-2xl tracking-tighter truncate" style={{ fontFamily: `"${fontDisplay}", ui-sans-serif` }}>{l.title}</div>
                    {l.description && <div className="text-xs text-white/70 truncate mt-1">{l.description}</div>}
                  </div>
                  <ArrowUpRight size={22} className="opacity-60 group-hover:opacity-100" style={{ color: accent }} />
                </button>
              ))}
            </div>
          )}

          {profile.show_shop !== false && products?.length > 0 && (
            <div className="col-span-6 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {profile.store_logo && <img src={profile.store_logo} alt="" className="h-8 max-w-[120px] object-contain" data-testid="bio-store-logo" />}
                  <div className="text-xs font-mono-k tracking-widest-k text-white/60">— SHOP</div>
                </div>
                {bundleEnabled && <div className="text-[10px] font-mono-k tracking-widest-k text-white/40 flex items-center gap-1"><Tag size={11} /> BUNDLE DEALS: 2 items = 5% · 3 items = 10% · 4+ = 15%</div>}
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {products.map(p => {
                  const inCart = cart[p.id] || 0;
                  return (
                    <div key={p.id} className={`border border-white/10 bg-black/40 ${cardClass}`} data-testid={`bio-prod-${p.id}`}>
                      {p.image ? <img src={p.image} alt="" className="w-full h-48 object-cover border-b border-white/10" /> : <div className="w-full h-48 bg-black/60 border-b border-white/10 flex items-center justify-center"><ShoppingBag size={40} className="text-white/50" /></div>}
                      <div className="p-4">
                        <div className="text-xl mb-1" style={{ fontFamily: `"${fontDisplay}", ui-sans-serif` }}>{p.name}</div>
                        <div className="text-xs font-mono-k text-white/70 mb-3 line-clamp-2">{p.description}</div>
                        <div className="flex items-center justify-between">
                          <div className="font-mono-k text-lg" style={{ color: accent }}>${p.price}</div>
                          {inCart > 0 ? (
                            <div className="flex items-center gap-2" data-testid={`bio-qty-${p.id}`}>
                              <button onClick={() => setQty(p.id, inCart - 1)} className="k-btn text-xs w-8 h-8 p-0 justify-center" data-testid={`bio-dec-${p.id}`}><Minus size={12} /></button>
                              <span className="font-mono-k text-sm w-6 text-center" style={{ color: accent }}>{inCart}</span>
                              <button onClick={() => setQty(p.id, inCart + 1)} className="k-btn text-xs w-8 h-8 p-0 justify-center" data-testid={`bio-inc-${p.id}`}><Plus size={12} /></button>
                            </div>
                          ) : (
                            <button onClick={() => addToCart(p)} className="k-btn primary text-xs" style={{ background: accent, color: "#050505", borderColor: accent }} data-testid={`bio-add-${p.id}`}>ADD TO CART <ShoppingCart size={14} /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-10 text-xs font-mono-k tracking-widest-k text-white/60">
          POWERED BY <a href="/" style={{ color: accent }}>KORPORATE</a>
        </div>
      </div>

      {/* Floating cart button */}
      {cartCount > 0 && !cartOpen && (
        <button onClick={() => setCartOpen(true)} className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 border-2 shadow-2xl font-mono-k text-xs tracking-widest-k" style={{ background: accent, color: "#050505", borderColor: "#050505" }} data-testid="cart-fab">
          <ShoppingCart size={18} weight="bold" />
          <span>{cartCount} · ${total.toFixed(2)}</span>
        </button>
      )}

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-md h-full bg-[#0A0D14] border-l border-white/10 overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="text-xs font-mono-k tracking-widest-k" style={{ color: accent }}>— YOUR CART · {cartCount} ITEM{cartCount === 1 ? "" : "S"}</div>
              <button onClick={() => setCartOpen(false)} className="k-btn text-xs" data-testid="cart-close"><X size={14} /></button>
            </div>

            {cartLines.length === 0 ? (
              <div className="text-white/60 font-mono-k text-sm">Your cart is empty.</div>
            ) : (
              <>
                <div className="space-y-3 mb-6">
                  {cartLines.map(l => (
                    <div key={l.id} className="border border-white/10 p-3 flex gap-3" data-testid={`cart-line-${l.id}`}>
                      {l.image ? <img src={l.image} alt="" className="w-16 h-16 object-cover border border-white/10" /> : <div className="w-16 h-16 border border-white/10 bg-black/60 flex items-center justify-center"><ShoppingBag size={22} className="text-white/40" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate" style={{ fontFamily: `"${fontDisplay}", ui-sans-serif` }}>{l.name}</div>
                        <div className="text-xs font-mono-k text-white/60">${l.price} each · line ${l.line_total.toFixed(2)}</div>
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => setQty(l.id, l.qty - 1)} className="k-btn text-xs w-7 h-7 p-0 justify-center"><Minus size={12} /></button>
                          <span className="font-mono-k text-xs w-6 text-center">{l.qty}</span>
                          <button onClick={() => setQty(l.id, l.qty + 1)} className="k-btn text-xs w-7 h-7 p-0 justify-center"><Plus size={12} /></button>
                          <button onClick={() => setQty(l.id, 0)} className="k-btn text-xs ml-auto" data-testid={`cart-remove-${l.id}`}><X size={12} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border border-white/10 p-4 mb-6">
                  <div className="flex justify-between text-sm font-mono-k mb-1"><span className="text-white/60">Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  {discountPct > 0 && (
                    <div className="flex justify-between text-sm font-mono-k mb-1" style={{ color: accent }}>
                      <span className="flex items-center gap-1"><Tag size={12} /> Bundle -{discountPct}%</span>
                      <span>-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-display text-2xl mt-3 pt-3 border-t border-white/10">
                    <span>Total</span><span style={{ color: accent }} data-testid="cart-total">${total.toFixed(2)}</span>
                  </div>
                  {nextTier && (
                    <div className="text-[10px] font-mono-k text-white/50 mt-2">
                      Add {nextTier.need} more product{nextTier.need === 1 ? "" : "s"} to unlock <span style={{ color: accent }}>{nextTier.pct}% off</span>
                    </div>
                  )}
                </div>

                <form onSubmit={checkout} className="space-y-3">
                  <input required placeholder="Your name" value={checkoutForm.buyer_name} onChange={e => setCheckoutForm({ ...checkoutForm, buyer_name: e.target.value })} className="k-input w-full" data-testid="cart-name" />
                  <input required type="email" placeholder="Your email (for receipt & delivery)" value={checkoutForm.buyer_email} onChange={e => setCheckoutForm({ ...checkoutForm, buyer_email: e.target.value })} className="k-input w-full" data-testid="cart-email" />
                  {needsShipping && (
                    <div className="border border-white/10 p-3 space-y-2" data-testid="cart-shipping">
                      <div className="text-[10px] font-mono-k tracking-widest-k" style={{ color: accent }}>— SHIPPING ADDRESS</div>
                      <input required placeholder="Recipient name" value={checkoutForm.shipping?.full_name || ""} onChange={e => setCheckoutForm({ ...checkoutForm, shipping: { ...(checkoutForm.shipping || {}), full_name: e.target.value } })} className="k-input w-full" data-testid="ship-name" />
                      <input required placeholder="Street address (line 1)" value={checkoutForm.shipping?.line1 || ""} onChange={e => setCheckoutForm({ ...checkoutForm, shipping: { ...(checkoutForm.shipping || {}), line1: e.target.value } })} className="k-input w-full" data-testid="ship-line1" />
                      <input placeholder="Apt / Suite / Unit (optional)" value={checkoutForm.shipping?.line2 || ""} onChange={e => setCheckoutForm({ ...checkoutForm, shipping: { ...(checkoutForm.shipping || {}), line2: e.target.value } })} className="k-input w-full" data-testid="ship-line2" />
                      <div className="grid grid-cols-2 gap-2">
                        <input required placeholder="City" value={checkoutForm.shipping?.city || ""} onChange={e => setCheckoutForm({ ...checkoutForm, shipping: { ...(checkoutForm.shipping || {}), city: e.target.value } })} className="k-input" data-testid="ship-city" />
                        <input placeholder="State / Region" value={checkoutForm.shipping?.region || ""} onChange={e => setCheckoutForm({ ...checkoutForm, shipping: { ...(checkoutForm.shipping || {}), region: e.target.value } })} className="k-input" data-testid="ship-region" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input required placeholder="ZIP / Postal" value={checkoutForm.shipping?.postal_code || ""} onChange={e => setCheckoutForm({ ...checkoutForm, shipping: { ...(checkoutForm.shipping || {}), postal_code: e.target.value } })} className="k-input" data-testid="ship-postal" />
                        <input required placeholder="Country" value={checkoutForm.shipping?.country || ""} onChange={e => setCheckoutForm({ ...checkoutForm, shipping: { ...(checkoutForm.shipping || {}), country: e.target.value } })} className="k-input" data-testid="ship-country" />
                      </div>
                      <input placeholder="Phone (optional)" value={checkoutForm.shipping?.phone || ""} onChange={e => setCheckoutForm({ ...checkoutForm, shipping: { ...(checkoutForm.shipping || {}), phone: e.target.value } })} className="k-input w-full" data-testid="ship-phone" />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setCheckoutForm({ ...checkoutForm, provider: "paypal" })} className={`k-btn text-xs justify-center ${checkoutForm.provider === "paypal" ? "primary" : ""}`} data-testid="cart-paypal">PAYPAL / CARD</button>
                    <button type="button" onClick={() => setCheckoutForm({ ...checkoutForm, provider: "nowpayments" })} className={`k-btn text-xs justify-center ${checkoutForm.provider === "nowpayments" ? "primary" : ""}`} data-testid="cart-crypto">USDT / CRYPTO</button>
                  </div>
                  <a href={`/u/${slug}/cart`} className="block text-center text-[10px] font-mono-k tracking-widest-k text-white/60 hover:text-white" data-testid="cart-fullpage-link">↗ OPEN FULL CART PAGE</a>
                  <button disabled={checkingOut} className="k-btn primary w-full justify-center" data-testid="cart-checkout">
                    {checkingOut ? "STARTING…" : `CHECKOUT · $${total.toFixed(2)}`}
                  </button>
                  <button type="button" onClick={clearCart} className="k-btn w-full justify-center text-xs" data-testid="cart-clear">EMPTY CART</button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicBio;
